"use client";

import { Image } from "@/components/ui/image";
import { useEffect, useState } from "react";
import {
  BarChart3,
  Check,
  FileCheck,
  ScanEye,
  Sparkles,
} from "@/components/icons";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { FocusedRegion } from "./focused-region";

const STEPS = [
  {
    icon: ScanEye,
    label: "Analyzing photos",
  },
  {
    icon: FileCheck,
    label: "Assessing condition",
  },
  {
    icon: BarChart3,
    label: "Estimating market value",
  },
] as const;

function useElapsedSeconds(): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const interval = setInterval(() => {
      setSeconds(Math.floor((Date.now() - started) / 1000));
    }, 250);
    return () => clearInterval(interval);
  }, []);
  return seconds;
}

function formatElapsed(seconds: number): string {
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

function useStepProgression(): number {
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev < STEPS.length - 1 ? prev + 1 : prev));
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  return activeStep;
}

interface AnalysisLoadingStateProps {
  imagePreviews: Array<string>;
  productTypeLabel?: string;
}

export function AnalysisLoadingState(props: AnalysisLoadingStateProps) {
  return (
    <FocusedRegion label="Analyzing your photos" block="nearest">
      <AnalysisProgressCard {...props} />
    </FocusedRegion>
  );
}

function AnalysisProgressCard({
  imagePreviews,
  productTypeLabel,
}: AnalysisLoadingStateProps) {
  const activeStep = useStepProgression();
  const elapsed = useElapsedSeconds();
  const [firstPreview] = imagePreviews;
  const progressPercent = Math.min(95, ((activeStep + 1) / STEPS.length) * 100);

  return (
    <Card className="mx-auto w-full max-w-xl sm:p-8" padding="roomy">
      <div className="flex items-center gap-4">
        <UploadThumbnail preview={firstPreview} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-base font-semibold">
              Analyzing {productTypeLabel ?? "your photos"}
            </p>
            <span className="tabular-nums text-sm text-muted-foreground">
              {formatElapsed(elapsed)}
            </span>
          </div>
          <Progress
            value={progressPercent}
            className="h-1.5"
            aria-label="Analysis progress"
          />
        </div>
      </div>
      <StepList activeStep={activeStep} />
      <p className="text-center text-xs text-muted-foreground">
        Usually takes 5–15 seconds. Keep this tab open.
      </p>
    </Card>
  );
}

function UploadThumbnail({ preview }: { preview: string | undefined }) {
  if (!preview) {
    return (
      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Sparkles className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border">
      <Image
        src={preview}
        alt="Your upload"
        fill
        className="object-cover"
        unoptimized
      />
      <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-primary/30 via-transparent to-primary/10" />
    </div>
  );
}

function StepList({ activeStep }: { activeStep: number }) {
  return (
    <ol className="space-y-3">
      {STEPS.map((step, index) => {
        const isActive = index === activeStep;
        const isDone = index < activeStep;
        return (
          <li
            key={step.label}
            className={cn(
              "flex items-center gap-3 transition-opacity duration-500",
              isDone ? "opacity-70" : isActive ? "opacity-100" : "opacity-40",
            )}
          >
            <StepIcon step={step} isActive={isActive} isDone={isDone} />
            <span
              className={cn(
                "text-sm",
                isActive
                  ? "font-medium text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function StepIcon({
  step,
  isActive,
  isDone,
}: {
  step: (typeof STEPS)[number];
  isActive: boolean;
  isDone: boolean;
}) {
  const Icon = step.icon;
  if (isDone) {
    return (
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-bg text-success-strong">
        <Check className="h-3.5 w-3.5" />
      </div>
    );
  }
  return (
    <div className="relative flex h-6 w-6 shrink-0 items-center justify-center">
      {isActive ? (
        <>
          <div className="absolute inset-0 animate-ping rounded-full bg-action-tint" />
          <Icon className="relative h-5 w-5 text-action" />
        </>
      ) : (
        <Icon className="h-5 w-5 text-muted-foreground" />
      )}
    </div>
  );
}
